import React from "react";

/**
 * Utility functions for wizard form handling
 * Shared across wizard components for form data management and localStorage
 */

/**
 * Saves form data to localStorage through a wizard config
 * @param formRef - Reference to the form
 * @param setStoredFormData - Function to update stored form data
 * @returns The saved form data, or null if save failed
 */
export const saveFormDataToLocalStorage = (
  formRef: any,
  setStoredFormData: (data: any) => void
): any | null => {
  if (!formRef?.current) {
    return null;
  }

  const formData = formRef.current.getFormData();
  if (!formData) {
    return null;
  }

  const dataToSave = {
    ...formData,
    template: formData.template,
  };

  setStoredFormData(dataToSave);
  return dataToSave;
};

/**
 * Handles form data persistence during wizard step advancement
 * Used when moving from step 0 to step 1 in wizards
 * @param config - Wizard config object with formRef and setStoredFormData
 * @param activeStepIndex - Current active step index
 * @returns true if save was successful, false otherwise
 */
export const handleWizardStepFormPersistence = (
  config: any,
  activeStepIndex: number
): boolean => {
  if (activeStepIndex !== 0) {
    return false;
  }

  if (!config.formRef?.current || !config.setStoredFormData) {
    return false;
  }

  const saved = saveFormDataToLocalStorage(
    config.formRef,
    config.setStoredFormData
  );

  return saved !== null;
};

/**
 * Creates a duplicate handler for outcome breakdown rows
 * @param setter - React state setter for the breakdown array
 * @returns A handler function that duplicates and adds a breakdown row
 */
export const createDuplicateBreakdownRowHandler = <
  T extends { outcomeBreakdownId: string }
>(
  setter: React.Dispatch<React.SetStateAction<T[]>>
) => {
  return (breakdownToDuplicate: T) => {
    const duplicatedBreakdown = {
      ...breakdownToDuplicate,
      outcomeBreakdownId: crypto.randomUUID(),
    };
    setter((prevBreakdowns) => [...prevBreakdowns, duplicatedBreakdown]);
  };
};
